import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ensureNetworks } from "@/lib/setup/ensureNetworks";
import { defaultScopeFor, FIELD_GROUPS, FIELD_KEYS } from "@/lib/setup/fields";
import { type ConnectionSplit, connectionSplit, mergeNetworks, previewStats, quotaNote, yearHistogram } from "@/lib/setup/preview";
import { cn } from "@/lib/utils";
import { isSettled, netKey, networksFor, type SeedRef, useSetupStore } from "@/stores/setup-store";
import type { ConceptScope, Recency } from "@/types/concept";
import { LoadingLine } from "./LoadingLine";
import { YearRange } from "./YearRange";

// Mirrors backend core/scope.py ConceptScope bounds (1800 … next year).
const YEAR_MIN = 1800;
const YEAR_MAX = new Date().getFullYear() + 1;
// Mockup colours: green / blue / grey (chart tokens keep both themes distinct).
const CONNECTIONS: [keyof ConnectionSplit, string, string][] = [
  ["seedsCite", "your seeds cite", "bg-chart-2"],
  ["citeSeeds", "cite your seeds", "bg-chart-1"],
  ["similar", "similar", "bg-muted-foreground/50"],
];
const LEAN: [Recency, string][] = [["classic", "Classics"], ["balanced", "Balanced"], ["recent", "Recent work"]];

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick}
      className={cn("rounded-full border px-3 py-1 text-sm", on ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted")}>
      {children}
    </button>
  );
}

/** ②'s right column: what Start would trawl (same `ensureNetworks` path, nothing
 *  posted) plus the scope controls. Scope is the caller's local state. */
export function PreviewPanel({ seeds, scope, onScope, remaining, children }: {
  seeds: SeedRef[];
  scope: ConceptScope;
  onScope: (s: ConceptScope) => void;
  remaining: number | null;
  children?: ReactNode; // footer (Next / Skip)
}) {
  const networks = useSetupStore((s) => s.networks);
  const resolved = useSetupStore((s) => s.resolved);
  const status = useSetupStore((s) => s.status);
  const recency = scope.recency;
  const [typing, setTyping] = useState(false);
  const [typed, setTyped] = useState({ from: "", to: "" });

  useEffect(() => { void ensureNetworks(seeds, recency); }, [seeds, recency]);

  const merged = useMemo(() => mergeNetworks(networksFor({ networks, resolved }, seeds, recency)), [networks, resolved, seeds, recency]);
  const stats = useMemo(() => previewStats(merged, scope), [merged, scope]);
  const buckets = useMemo(() => yearHistogram(merged, scope), [merged, scope]);
  const split = useMemo(() => connectionSplit(merged, scope), [merged, scope]);
  const note = quotaNote(stats.inRange, remaining);
  const statusOf = (s: SeedRef) => status[netKey(s.key, recency)];
  const empty = seeds.filter((s) => statusOf(s) === "empty");
  const failed = seeds.filter((s) => statusOf(s) === "failed");
  const retrying = seeds.filter((s) => statusOf(s) === "retrying").length;
  const settled = seeds.filter((s) => isSettled(statusOf(s))).length;

  function applyTyped() {
    const parse = (v: string) => {
      const n = Number(v);
      return v.trim() === "" || !Number.isInteger(n) ? undefined : Math.min(YEAR_MAX, Math.max(YEAR_MIN, n));
    };
    let a = parse(typed.from), b = parse(typed.to);
    if (a != null && b != null && a > b) [a, b] = [b, a];
    onScope({ ...scope, year_min: a, year_max: b });
    setTyping(false);
  }

  return (
    <aside className="flex flex-col gap-6 rounded-xl border bg-muted/30 p-5" aria-label="Preview">
      <div aria-live="polite">
        <p className="text-sm text-muted-foreground">Your seeds would bring in</p>
        <p><span className="text-4xl font-semibold">{seeds.length ? stats.inRange : "—"}</span> papers to grade</p>
        {stats.outOfRange > 0 && <p className="text-sm text-muted-foreground">+{stats.outOfRange} outside your years, kept but hidden</p>}
        {note && <p className="text-sm">{note}</p>}
        {seeds.length > 0 && settled < seeds.length && (
          <LoadingLine className="mt-2" progress={{ done: settled, total: seeds.length }}>
            Looking at what your seeds connect to… ({settled} of {seeds.length})
          </LoadingLine>
        )}
        {retrying > 0 && (
          <LoadingLine className="mt-2">
            Semantic Scholar is busy. Retrying {retrying === 1 ? "1 seed" : `${retrying} seeds`}…
          </LoadingLine>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between">
          <span className="font-medium">Years</span>
          <span className="text-sm text-muted-foreground">
            {scope.year_min ?? "Any"} – {scope.year_max ?? "now"} · drag to change
          </span>
        </div>
        <YearRange buckets={buckets} from={scope.year_min} to={scope.year_max}
          onChange={(year_min, year_max) => onScope({ ...scope, year_min, year_max })} />
        {typing ? (
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col text-sm">From<Input type="number" className="w-24" value={typed.from}
              onChange={(e) => setTyped({ ...typed, from: e.target.value })} /></label>
            <label className="flex flex-col text-sm">To<Input type="number" className="w-24" value={typed.to}
              onChange={(e) => setTyped({ ...typed, to: e.target.value })} /></label>
            <Button size="sm" variant="outline" onClick={applyTyped}>Apply years</Button>
          </div>
        ) : (
          <button type="button" className="w-fit text-sm text-muted-foreground underline"
            onClick={() => { setTyped({ from: String(scope.year_min ?? ""), to: String(scope.year_max ?? "") }); setTyping(true); }}>
            Type years
          </button>
        )}
      </div>

      {stats.inRange > 0 && (
        <div className="flex flex-col gap-2">
          <span className="font-medium">How they connect</span>
          <div className="flex h-2.5 gap-0.5 overflow-hidden rounded" aria-hidden>
            {CONNECTIONS.map(([k, , color]) => split[k] > 0 && (
              <div key={k} className={color} style={{ flexGrow: split[k] }} />
            ))}
          </div>
          <ul className="flex flex-wrap gap-x-3.5 gap-y-1 text-sm text-muted-foreground">
            {CONNECTIONS.map(([k, label, color]) => (
              <li key={k} className="flex items-center gap-1.5">
                <span aria-hidden className={`size-2 rounded-sm ${color}`} />{split[k]}% {label}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <span className="font-medium">Lean toward</span>
        <div className="flex flex-wrap gap-2">
          {LEAN.map(([r, label]) => (
            <Chip key={r} on={(scope.recency ?? "balanced") === r} onClick={() => onScope({ ...scope, recency: r })}>{label}</Chip>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span className="font-medium">Field</span>
        <div className="flex flex-wrap gap-2">
          {FIELD_KEYS.map((k) => (
            <Chip key={k} on={scope.field === k} onClick={() => onScope(defaultScopeFor(k))}>{FIELD_GROUPS[k].label}</Chip>
          ))}
          <Chip on={!scope.field} onClick={() => onScope({ ...scope, field: undefined })}>Any field</Chip>
        </div>
      </div>

      {empty.length > 0 && (
        <div className="text-sm">
          <p>We couldn't find citations for these. Each stays a seed but won't be trawled:</p>
          <ul className="list-disc pl-5">{empty.map((s) => <li key={s.key}>{s.title}</li>)}</ul>
        </div>
      )}
      {failed.length > 0 && (
        <div className="text-sm">
          <p>Semantic Scholar didn't answer for these, even after retrying. Each stays a seed but won't be trawled (change your selection to try again):</p>
          <ul className="list-disc pl-5">{failed.map((s) => <li key={s.key}>{s.title}</li>)}</ul>
        </div>
      )}

      {children}
    </aside>
  );
}
